import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import ThreadsCafeMapSection from "../template/magazine/ThreadsCafeMapSection";
import { dbService } from "../firebaseInstance";
import { useTranslation } from "react-i18next";

const MAG_ROOT = "magazines";
const MAG_ID = "monthlyThreads";
const ISSUE_ID = "2025-09"; // 9월호 고정

const ThreadsCafeMap = ({
	fullScreen = true,
	containerSx = {},
	initialParticipants,
	initialCollections,
	initialShowCafeRegistrationModal,
	onToggleCafeRegistrationModal,
}) => {
	useTranslation();

	const [participants, setParticipants] = useState(() =>
		Array.isArray(initialParticipants) ? initialParticipants : []
	);
	const [collections, setCollections] = useState(() =>
		Array.isArray(initialCollections) ? initialCollections : []
	);
	const [showCafeRegistrationModal, setShowCafeRegistrationModal] = useState(
		!!initialShowCafeRegistrationModal
	);
	const [loading, setLoading] = useState(true);
	const [collectionInfoById, setCollectionInfoById] = useState({});
	const [userInfoById, setUserInfoById] = useState({});

	// 9월호 데이터 로드
	useEffect(() => {
		if (Array.isArray(initialParticipants)) {
			setParticipants(initialParticipants);
		}
	}, [initialParticipants]);

	useEffect(() => {
		if (Array.isArray(initialCollections)) {
			setCollections(initialCollections);
		}
	}, [initialCollections]);

	useEffect(() => {
		if (typeof initialShowCafeRegistrationModal === "boolean") {
			setShowCafeRegistrationModal(initialShowCafeRegistrationModal);
		}
	}, [initialShowCafeRegistrationModal]);

	const updateCafeRegistrationModal = useCallback(
		(nextValue) => {
			setShowCafeRegistrationModal(nextValue);
			if (typeof onToggleCafeRegistrationModal === "function") {
				onToggleCafeRegistrationModal(nextValue);
			}
		},
		[onToggleCafeRegistrationModal]
	);

	useEffect(() => {
		const shouldFetchData =
			!Array.isArray(initialParticipants) ||
			initialParticipants.length === 0 ||
			!Array.isArray(initialCollections) ||
			initialCollections.length === 0;

		if (!shouldFetchData) {
			setLoading(false);
			return;
		}

		const loadIssueData = async () => {
			setLoading(true);
			try {
				// 먼저 issues 컬렉션 시도, 없으면 issue 컬렉션 fallback
				let issueDoc;
				const issuesRef = dbService
					.collection(MAG_ROOT)
					.doc(MAG_ID)
					.collection("issues")
					.doc(ISSUE_ID);

				issueDoc = await issuesRef.get();

				if (!issueDoc.exists) {
					// fallback to legacy 'issue' collection
					const issueRef = dbService
						.collection(MAG_ROOT)
						.doc(MAG_ID)
						.collection("issue")
						.doc(ISSUE_ID);
					issueDoc = await issueRef.get();
				}

				if (!issueDoc.exists) {
					console.error("Issue document not found");
					setLoading(false);
					return;
				}

				const issueData = issueDoc.data();
				const collectionPath = issueDoc.ref.parent.id; // 'issues' or 'issue'

				// items 로드
				const itemsSnapshot = await dbService
					.collection(MAG_ROOT)
					.doc(MAG_ID)
					.collection(collectionPath)
					.doc(ISSUE_ID)
					.collection("items")
					.orderBy("order", "asc")
					.get();

				const items = itemsSnapshot.docs.map(doc => ({
					id: doc.id,
					...doc.data()
				}));

				// collections 로드
				const colIds = Array.from(
					new Set(items.map(it => it.collectionId).filter(Boolean))
				);

				if (colIds.length > 0) {
					const colSnapshots = await Promise.all(
						colIds.map(id =>
							dbService.collection("collection").doc(id).get()
						)
					);

					const colInfoById = {};
					colSnapshots.forEach((snap, idx) => {
						const id = colIds[idx];
						if (snap.exists) {
							colInfoById[id] = { id, ...snap.data() };
						}
					});
					setCollectionInfoById(colInfoById);

					// collections 정보 설정
					const collectionsData = colIds
						.map(id => colInfoById[id])
						.filter(Boolean)
						.map(col => ({
							id: col.id,
							title: col.title || col.name || col.id
						}));
					setCollections(collectionsData);
				}

				// users 로드
				const userIds = Array.from(
					new Set([
						...(issueData?.userIdList || []),
						...items.map(it => it.userId).filter(Boolean)
					].map(String))
				);

				if (userIds.length > 0) {
					const userSnapshots = await Promise.all(
						userIds.map(id =>
							dbService.collection("user").doc(id).get()
						)
					);

					const userInfo = {};
					userSnapshots.forEach((snap, idx) => {
						const id = userIds[idx];
						if (snap.exists) {
							userInfo[id] = { id, ...snap.data() };
						}
					});
					setUserInfoById(userInfo);

					// participants 설정 (셔플 적용)
					const participantsMapped = userIds.map(uid => {
						const userDoc = userInfo[uid] || { id: uid };
						// 컬렉션에서 해당 유저의 아바타를 폴백으로 추출
						let fallbackAvatar = null;
						const firstCol = items.find(it => String(it.userId) === String(uid));
						if (firstCol && collectionInfoById[firstCol.collectionId]) {
							const colDoc = collectionInfoById[firstCol.collectionId];
							fallbackAvatar =
								colDoc?.profileImage ||
								colDoc?.profile?.threads_profile_picture_url ||
								null;
						}
						return { ...userDoc, __fallbackAvatar: fallbackAvatar };
					});

					// 일관된 셔플 (issueId 기반)
					const shuffled = [...participantsMapped];
					const seed = ISSUE_ID.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
					let random = seed;
					for (let i = shuffled.length - 1; i > 0; i--) {
						random = (random * 1664525 + 1013904223) % 4294967296;
						const j = Math.floor((random / 4294967296) * (i + 1));
						[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
					}

					setParticipants(shuffled);
				}

			} catch (error) {
				console.error("Error loading issue data:", error);
			} finally {
				setLoading(false);
			}
		};

		loadIssueData();
	}, [initialParticipants, initialCollections]);

	// 카페 등록 플로우에서 돌아온 경우 처리
	useEffect(() => {
		const cafeRegistrationReturn = sessionStorage.getItem("cafe_registration_return");
		if (cafeRegistrationReturn === "true") {
			sessionStorage.removeItem("cafe_registration_return");
			sessionStorage.removeItem("magazine_issue_id");
			// 카페 등록 모달을 자동으로 열기
			updateCafeRegistrationModal(true);
		}
	}, [updateCafeRegistrationModal]);

	const containerStyles = {
		width: fullScreen ? "100vw" : "100%",
		maxWidth: "100%",
		height: fullScreen ? "100vh" : "auto",
		minHeight: fullScreen ? "100vh" : "auto",
		position: "relative",
		overflow: "hidden",
		boxSizing: "border-box",
		...(fullScreen
			? {
					left: 0,
					top: 0,
			  }
			: {}),
		...containerSx,
	};

	return (
		<Box sx={containerStyles}>
			{loading ? (
				<Box
					sx={{
						width: "100%",
						height: "100%",
						display: "flex",
						alignItems: "center",
						justifyContent: "center"
					}}
				>
					<Typography color="text.secondary">
						로딩 중...
					</Typography>
				</Box>
			) : (
				<ThreadsCafeMapSection
					participants={participants}
					collections={collections}
					showCafeRegistrationModal={showCafeRegistrationModal}
					setShowCafeRegistrationModal={updateCafeRegistrationModal}
					isFull={fullScreen}
				/>
			)}
		</Box>
	);
};

export default ThreadsCafeMap;
